import { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { PipelineBoard } from '../components/workflow/PipelineBoard';
import { ApprovalCard } from '../components/workflow/ApprovalCard';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useWeeks, useCreateWeek, useApproveStage, useRejectStage, usePendingWeeks } from '../hooks/useWorkflow';

export function DashboardPage() {
  const { data: weeksData, isLoading: weeksLoading } = useWeeks();
  const { data: pendingData } = usePendingWeeks();
  const createWeek = useCreateWeek();
  const approveStage = useApproveStage();
  const rejectStage = useRejectStage();
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const weeks = weeksData?.weeks ?? [];
  const pendingWeeks = pendingData?.weeks ?? [];

  const handleApprove = async (weekId: string) => {
    setApprovingId(weekId);
    try {
      await approveStage.mutateAsync(weekId);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (weekId: string) => {
    const feedback = window.prompt('What needs to change?');
    if (feedback === null) return;
    await rejectStage.mutateAsync({ weekId, feedback });
  };

  const handleNewWeek = async () => {
    await createWeek.mutateAsync({});
  };

  return (
    <Layout>
      <Header
        title="Dashboard"
        subtitle="Content pipeline for Falcon Incentives"
        actions={
          <Button onClick={handleNewWeek} loading={createWeek.isPending}>
            + New Week
          </Button>
        }
      />

      {pendingWeeks.length > 0 && (
        <section className="mb-8">
          <h2 className="font-display font-semibold text-sm text-gray-500 uppercase tracking-wide mb-3">
            Needs Your Approval
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {pendingWeeks.map((week) => (
              <ApprovalCard
                key={week.id}
                week={week}
                onApprove={handleApprove}
                onReject={handleReject}
                loading={approvingId === week.id}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-display font-semibold text-sm text-gray-500 uppercase tracking-wide mb-4">
          Pipeline
        </h2>
        {weeksLoading ? (
          <Card>
            <div className="text-center py-8 text-gray-400 font-body text-sm">Loading...</div>
          </Card>
        ) : (
          <Card padded={false} className="p-6 overflow-hidden">
            <PipelineBoard weeks={weeks} />
          </Card>
        )}
      </section>
    </Layout>
  );
}
